/** @jsxImportSource @emotion/react */
// @ts-nocheck
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { Spin, Input as AntInput } from 'antd'
import { Suspense, useRef } from 'react'
import { useSelector, useDispatch } from 'src/hooks/useRedux'
import accountActions from '@sportninja/common/actions/account'
import { useForm } from 'react-hook-form'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import Text from 'src/components/ts-components/Text'
import { useDaySmartIntegration } from './useDaySmartIntegration'
import { useHockeyUSAIntegration } from './useHockeyUSAIntegration'
import DaySmartIntegrationCard from './DaySmartIntegrationCard'
import HockeyUSAIntegrationCard from './HockeyUSAIntegrationCard'
import {
  HockeyCanadaIntegrationCard,
  useHockeyCanadaIntegration,
} from './HockeyCanada'

const { Search } = AntInput

export const Integrations = ({ setCurrentPage }) => {
  const dispatch = useDispatch()
  const account = useSelector((state: any) => state.account._meAccount)
  const user = useSelector((state: any) => state.auth.user)

  const fetchMe = () => {
    dispatch(accountActions.fetchAccount.request(null, null, user?.account_id))
  }
  const isUltimateAccount = account?.subscription_type?.id === 3
  const accountTypeName =
    account?.subscription_type?.id === 1
      ? 'Free'
      : account?.subscription_type?.id === 2
      ? 'Pro'
      : ''
  const {
    control,
    setValue: setFormValue,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {},
  })

  const integrationModalRef = useRef(null)

  // DaySmart integration hook
  const daySmartIntegration = useDaySmartIntegration({
    accountUid: account?.uid,
    setFormValue,
    integrationModalRef,
    fetchMe,
    watch,
    integrations: account?.integrations,
  })

  // USA Hockey integration hook
  const hockeyUSAIntegration = useHockeyUSAIntegration({
    accountUid: account?.uid,
    integrations: account?.integrations,
  })

  // Hockey Canada integration hook
  const {
    isHockeyCanadaConnected,
    isHockeyCanadaEnabled,
    showHockeyCanadaWarning,
    isUpdatingHockeyCanada,
    handleHockeyCanadaToggle,
    confirmHockeyCanadaEnable,
    cancelHockeyCanadaEnable,
  } = useHockeyCanadaIntegration({
    accountUid: account?.uid,
    integrations: account?.integrations,
  })

  // Extract state values for easier access
  const {
    acceptedTerms,
    isLoading,
    isLoadingTest,
    testSuccess,
    testSubmitted,
    apiKey,
    apiKeyError,
    secretKey,
    secretKeyError,
    error,
    actions: daySmartActions,
    onPressTest,
    onPressSubmit,
  } = daySmartIntegration

  const {
    isHockeyUSAConnected,
    isHockeyUSAEnabled,
    showHockeyUSAWarning,
    pendingHockeyUSAEnabled,
    handleHockeyUSAToggle,
    confirmHockeyUSAEnable,
    cancelHockeyUSAEnable,
  } = hockeyUSAIntegration

  return (
    <>
      <div
        css={css`
          background-color: ${colors.HEADER};
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${colors.SOFT_STEEL};
          max-width: 1080px;
          margin: 0 auto;
          margin-top: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
              INTEGRATIONS
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              Connect With Our Partners And Enhance Your Experience.
            </Text>
          </div>
        </div>
        <Suspense fallback={<Spin />}>
          <div
            css={css`
              margin-top: 16px;
              padding-top: 16px;
              border-top: 1px solid ${colors.SOFT_STEEL};
              * {
                border-color: ${colors.SOFT_STEEL} !important;
              }
            `}
          >
            {/* Two cards per row; collapses to one on narrow viewports. */}
            <div
              css={css`
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: 16px;
                @media (max-width: 720px) {
                  grid-template-columns: minmax(0, 1fr);
                }
              `}
            >
              {/* Daysmart and Hockey USA are not offered on Canlan. */}
              {!isCanlan ? (
                <DaySmartIntegrationCard
                  isUltimateAccount={isUltimateAccount}
                  apiKey={apiKey}
                  secretKey={secretKey}
                  apiKeyError={apiKeyError}
                  secretKeyError={secretKeyError}
                  error={error}
                  acceptedTerms={acceptedTerms}
                  isLoading={isLoading}
                  isLoadingTest={isLoadingTest}
                  testSubmitted={testSubmitted}
                  testSuccess={testSuccess}
                  control={control}
                  integrationModalRef={integrationModalRef}
                  setCurrentPage={setCurrentPage}
                  actions={daySmartActions}
                  onPressTest={onPressTest}
                  onPressSubmit={onPressSubmit}
                  accountTypeName={accountTypeName}
                />
              ) : null}
              {!isCanlan ? (
                <HockeyUSAIntegrationCard
                  isUltimateAccount={isUltimateAccount}
                  isHockeyUSAConnected={isHockeyUSAConnected}
                  isHockeyUSAEnabled={isHockeyUSAEnabled}
                  showHockeyUSAWarning={showHockeyUSAWarning}
                  handleHockeyUSAToggle={handleHockeyUSAToggle}
                  confirmHockeyUSAEnable={confirmHockeyUSAEnable}
                  cancelHockeyUSAEnable={cancelHockeyUSAEnable}
                  setCurrentPage={setCurrentPage}
                  accountTypeName={accountTypeName}
                />
              ) : null}
              {/* Hockey Canada is a Canlan-only integration. */}
              {isCanlan ? (
                <HockeyCanadaIntegrationCard
                  isUltimateAccount={isUltimateAccount}
                  isHockeyCanadaConnected={isHockeyCanadaConnected}
                  isHockeyCanadaEnabled={isHockeyCanadaEnabled}
                  showHockeyCanadaWarning={showHockeyCanadaWarning}
                  isUpdatingHockeyCanada={isUpdatingHockeyCanada}
                  handleHockeyCanadaToggle={handleHockeyCanadaToggle}
                  confirmHockeyCanadaEnable={confirmHockeyCanadaEnable}
                  cancelHockeyCanadaEnable={cancelHockeyCanadaEnable}
                  setCurrentPage={setCurrentPage}
                  accountTypeName={accountTypeName}
                />
              ) : null}
            </div>
          </div>
        </Suspense>
      </div>
    </>
  )
}
